import { datadogRum } from '@datadog/browser-rum';
import { matchPath } from 'react-router-dom';

import { MarketId } from 'types/market-id.enum';
import { UserType } from 'types/shared/user-type.enum';
import { Route } from 'types/route.type';

type DatadogRumUser = {
  id: string;
  name: string;
  companyId: string;
  companyType: string;
  marketId: MarketId;
  userType: UserType;
};

// Client token/application ID are public by design, but only send data once both are configured for the current env
const isConfigured = (): boolean => !!import.meta.env.VITE_DATADOG_APPLICATION_ID && !!import.meta.env.VITE_DATADOG_CLIENT_TOKEN;

const initializeDatadogRum = (): void => {
  if (!isConfigured()) return;

  datadogRum.init({
    applicationId: `${import.meta.env.VITE_DATADOG_APPLICATION_ID}`,
    clientToken: `${import.meta.env.VITE_DATADOG_CLIENT_TOKEN}`,
    site: import.meta.env.VITE_DATADOG_SITE || 'datadoghq.com',
    service: import.meta.env.VITE_DATADOG_SERVICE || 'myewos-login',
    env: import.meta.env.VITE_DATADOG_ENV,
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0, // enable only after Session Replay privacy sign-off
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    trackViewsManually: true,
    defaultPrivacyLevel: 'mask-user-input',
    // Disabled because these trace headers fail the API gateway's CORS preflight. RUM remains enabled, but requests cannot link to
    // backend APM traces. Re-enable after the gateway allows Datadog/W3C headers and backend APM propagates them.
    // allowedTracingUrls: [`${import.meta.env.VITE_REST_API_URL}`],
  });
};

const setDatadogRumUser = (user: DatadogRumUser): void => {
  if (!isConfigured()) return;
  datadogRum.setUser(user);
};

const clearDatadogRumUser = (): void => {
  if (!isConfigured()) return;
  datadogRum.clearUser();
};

const addDatadogRumError = (error: unknown, context?: Record<string, unknown>): void => {
  if (!isConfigured()) return;
  datadogRum.addError(error, context);
};

// Mirrors the matching dashboard.tsx's <Switch> does at render time, since react-router-dom v5 has no matchRoutes API
const trackDatadogRumView = (pathname: string, routes: Route[]): void => {
  if (!isConfigured()) return;

  const matchedRoute = routes.find((route) => matchPath(pathname, { path: route.path, exact: true }));
  datadogRum.startView({ name: matchedRoute?.id ?? matchedRoute?.path ?? pathname });
};

const addDatadogRumAction = (name: string, context?: Record<string, unknown>): void => {
  if (!isConfigured()) return;
  datadogRum.addAction(name, context);
};

export { initializeDatadogRum, setDatadogRumUser, clearDatadogRumUser, addDatadogRumError, trackDatadogRumView, addDatadogRumAction };
